import React from "react";
import { Car, CloudRain, Waves, TrafficCone, Ban, Users, CircleAlert, MapPin, Camera, Send, Loader2, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { reportIncident, uploadIncidentPhoto, fileUrl } from "../../lib/api";

const TYPES = [
  { key: "Accident", Icon: CircleAlert, sev: "high", tint: "#FF5C7A" },
  { key: "Road Closure", Icon: Ban, sev: "high", tint: "#FF5C7A" },
  { key: "Gathering / Crowd", Icon: Users, sev: "medium", tint: "#FFC726" },
  { key: "Flooding", Icon: Waves, sev: "high", tint: "#38BDF8" },
  { key: "Stalled Vehicle", Icon: Car, sev: "medium", tint: "#FFC726" },
  { key: "Debris / Hazard", Icon: TrafficCone, sev: "medium", tint: "#FFC726" },
];

const FieldReport = ({ officer, onDone }) => {
  const [type, setType] = React.useState(null);
  const [note, setNote] = React.useState("");
  const [location, setLocation] = React.useState("");
  const [coords, setCoords] = React.useState(null);
  const [photo, setPhoto] = React.useState(null); // {path, preview}
  const [uploading, setUploading] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const pin = () => {
    if (!navigator.geolocation) return toast.error("GPS not available");
    navigator.geolocation.getCurrentPosition(
      (p) => { const c = [p.coords.latitude, p.coords.longitude]; setCoords(c); setLocation(location || `GPS ${c[0].toFixed(4)}, ${c[1].toFixed(4)}`); toast.success("GPS pinned"); },
      () => toast.error("Enable GPS to pin location"), { enableHighAccuracy: true, timeout: 8000 });
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try { const r = await uploadIncidentPhoto(f); setPhoto({ path: r.path }); toast.success("Photo attached"); }
    catch { toast.error("Photo upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    const t = TYPES.find((x) => x.key === type);
    setSending(true);
    try {
      await reportIncident({
        type, description: note.trim() || type, location: location.trim() || "Barbados",
        severity: t?.sev || "medium", source: "field", reporter_name: officer?.callsign || "Field Officer",
        lat: coords?.[0], lon: coords?.[1], photo_path: photo?.path || null,
      });
      setSent(true); toast.success("Logged to Command Centre");
      setTimeout(() => { setSent(false); setType(null); setNote(""); setLocation(""); setCoords(null); setPhoto(null); onDone && onDone(); }, 1500);
    } catch { toast.error("Failed to log — retry"); }
    finally { setSending(false); }
  };

  if (sent) return (
    <div className="flex h-full flex-col items-center justify-center px-8 text-center">
      <div className="go-fade-up flex h-20 w-20 items-center justify-center rounded-full bg-[#22E0A1]/15"><CheckCircle2 size={44} color="#22E0A1" strokeWidth={2.4} /></div>
      <h2 className="font-head mt-5 text-2xl font-extrabold text-white">Logged</h2>
      <p className="mt-2 text-sm text-[#8194B2]">Report is live on the Command Centre dashboard.</p>
    </div>
  );

  return (
    <div className="go-scroll min-h-full overflow-y-auto px-5 pb-40 pt-[max(env(safe-area-inset-top),16px)]">
      <header className="go-fade-up mb-4"><h1 className="font-head text-2xl font-extrabold text-white">Log Incident</h1>
        <p className="mt-1 text-sm text-[#8194B2]">Filing as <span className="font-semibold text-[#5B9BFF]">{officer?.callsign}</span></p></header>

      <div className="go-grid2">
        {TYPES.map(({ key, Icon, tint }) => {
          const on = type === key;
          return (
            <button key={key} onClick={() => setType(key)} className={`flex flex-col items-start gap-2.5 rounded-2xl border-2 p-4 transition-all active:scale-95 ${on ? "border-[#5B9BFF] bg-[#1E4DB7]/20" : "go-card border-transparent"}`} data-testid={`field-type-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${tint}22` }}><Icon size={22} color={tint} strokeWidth={2.3} /></div>
              <span className="font-head text-[13px] font-bold text-white">{key}</span>
            </button>
          );
        })}
      </div>

      {type && (
        <div className="go-fade-up mt-5 space-y-4">
          <div className="flex gap-2">
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location / landmark" className="go-card w-full rounded-2xl px-4 py-3 text-[15px] font-medium text-white outline-none placeholder:text-[#5f7193]" data-testid="field-location" />
            <button onClick={pin} className={`flex shrink-0 items-center justify-center rounded-2xl px-4 active:scale-95 ${coords ? "bg-[#22E0A1] text-[#06133A]" : "bg-[#1E4DB7] text-white"}`} data-testid="field-gps"><MapPin size={20} strokeWidth={2.4} /></button>
          </div>

          <label className="go-card flex cursor-pointer items-center gap-3 rounded-2xl px-4 py-3" data-testid="field-photo-label">
            {photo ? <img src={fileUrl(photo.path)} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/10">{uploading ? <Loader2 size={20} className="animate-spin text-[#5B9BFF]" /> : <Camera size={20} color="#5B9BFF" />}</span>}
            <span className="flex-1 text-sm font-semibold text-[#A9B8D2]">{photo ? "Photo attached — tap to replace" : uploading ? "Uploading…" : "Attach a photo"}</span>
            {photo && <X size={16} className="text-[#67799A]" onClick={(e) => { e.preventDefault(); setPhoto(null); }} />}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="field-photo-input" />
          </label>

          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Situation report / details" className="go-card w-full resize-none rounded-2xl px-4 py-3 text-[15px] font-medium text-white outline-none placeholder:text-[#5f7193]" data-testid="field-note" />

          <button onClick={submit} disabled={sending || uploading} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF5C7A] py-4 text-base font-bold text-white transition-transform active:scale-[0.98] disabled:opacity-60" data-testid="field-submit">
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} strokeWidth={2.5} />}{sending ? "Logging…" : "Log to Command"}
          </button>
        </div>
      )}
    </div>
  );
};

export default FieldReport;
